import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CircleMarker, MapContainer, Marker, Polygon, Polyline, TileLayer, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import buffer from "@turf/buffer";
import api from "../api/axios";
import AdminShell from "./AdminShell";
import GpsManagementTabs from "./GpsManagementTabs";
import BookingAlertConfigurationPanel, {
  DEFAULT_BOOKING_ALERT_CONFIGURATION,
} from "./BookingAlertConfigurationPanel";
import { useAuth } from "../context/AuthContext";
import useMonitoringNotificationRefresh from "../hooks/useMonitoringNotificationRefresh";
import { getMonitoringSeverity } from "../utils/monitoringSeverity";
import { GPS_DISABLED_MESSAGE, GPS_UNAVAILABLE_MESSAGE, NOT_AVAILABLE } from "./gpsManagementConstants";
import "leaflet/dist/leaflet.css";
import "../styles/admin.css";

export { GPS_DISABLED_MESSAGE, GPS_UNAVAILABLE_MESSAGE, NOT_AVAILABLE } from "./gpsManagementConstants";

const FLEET_POLL_MS = 10000;
const SELECTED_POLL_MS = 5000;
const HIDDEN_POLL_MS = 30000;
const MAX_HISTORY_MS = 7 * 24 * 60 * 60 * 1000;
const EMPTY_FLEET_RECORDS = Object.freeze([]);
const MONITORING_BOOKING_STATUSES = new Set([
  "active", "ongoing",
]);
const MONITORING_PAYMENT_STATUSES = new Set(["verified", "downpayment_paid", "fully_paid"]);
const MANILA_OFFSET = "+08:00";

function bookingBoundaryMs(booking, boundary) {
  const start = boundary === "start";
  const dateValue = booking?.[start ? "startDate" : "endDate"];
  const timeValue = String(booking?.[start ? "startTime" : "endTime"] || "").trim();
  const dateText = dateValue instanceof Date ? dateValue.toISOString() : String(dateValue || "").trim();
  const datePart = /^\d{4}-\d{2}-\d{2}/.exec(dateText)?.[0];
  if (datePart && /^\d{2}:\d{2}(?::\d{2})?$/.test(timeValue)) {
    const normalizedTime = timeValue.length === 5 ? `${timeValue}:00` : timeValue;
    return Date.parse(`${datePart}T${normalizedTime}${MANILA_OFFSET}`);
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateText)) {
    return Date.parse(`${dateText}T${start ? "00:00:00.000" : "23:59:59.999"}${MANILA_OFFSET}`);
  }
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?$/.test(dateText)) {
    return Date.parse(`${dateText}${MANILA_OFFSET}`);
  }
  return dateValue ? new Date(dateValue).getTime() : Number.NaN;
}

export function validCoordinates(value) {
  const numericCoordinate = (coordinate) => {
    if (typeof coordinate === "number") return coordinate;
    if (typeof coordinate === "string" && coordinate.trim() !== "") return Number(coordinate);
    return Number.NaN;
  };
  const latitude = numericCoordinate(value?.latitude);
  const longitude = numericCoordinate(value?.longitude);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude) ||
      latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180 ||
      (latitude === 0 && longitude === 0)) return null;
  return { latitude, longitude };
}

export function leafletPosition(value) {
  const coordinate = validCoordinates(value);
  return coordinate ? [coordinate.latitude, coordinate.longitude] : null;
}

function leafletPath(values, minimumPoints = 2) {
  if (!Array.isArray(values)) return [];
  const positions = values.map(leafletPosition).filter(Boolean);
  return positions.length >= minimumPoints ? positions : [];
}

export function formatValue(value, suffix = "") {
  if (value === null || value === undefined || value === "") return NOT_AVAILABLE;
  return `${value}${suffix}`;
}

function formatTime(value) {
  const parsed = value ? new Date(value) : null;
  return parsed && Number.isFinite(parsed.getTime()) ? parsed.toLocaleString() : NOT_AVAILABLE;
}

export function routeDeviationCorridor(route, radiusMeters) {
  const points = Array.isArray(route) ? route.map(validCoordinates).filter(Boolean) : [];
  const radius = Number(radiusMeters);
  if (points.length < 2 || !Number.isFinite(radius) || radius < 50) return [];
  try {
    const feature = buffer({
      type: "Feature",
      properties: {},
      geometry: {
        type: "LineString",
        coordinates: points.map((point) => [point.longitude, point.latitude]),
      },
    }, radius, { units: "meters", steps: 24 });
    const geometry = feature?.geometry;
    const rings = geometry?.type === "Polygon" ? geometry.coordinates :
      geometry?.type === "MultiPolygon" ? geometry.coordinates.flat() : [];
    const outerRing = rings.reduce((longest, ring) => ring.length > longest.length ? ring : longest, []);
    return outerRing.map((pair) => validCoordinates({ latitude: pair[1], longitude: pair[0] })).filter(Boolean);
  } catch {
    return [];
  }
}

function formatSchedule(booking) {
  const startMs = bookingBoundaryMs(booking, "start");
  const endMs = bookingBoundaryMs(booking, "end");
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) {
    return "Schedule not available";
  }
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Manila", month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  });
  return `${formatter.format(startMs)} \u2013 ${formatter.format(endMs)}`;
}

function formatUpcomingStart(value) {
  const start = new Date(value || "");
  if (!Number.isFinite(start.getTime())) return "Schedule unavailable";
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Manila",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(start);
}

function titleCase(value) {
  const text = String(value || "").replace(/_/g, " ").trim();
  return text ? text.replace(/\b\w/g, (letter) => letter.toUpperCase()) : NOT_AVAILABLE;
}

function displayGpsStatus(record) {
  return record?.freshnessStatus || record?.trackingStatus || record?.gpsStatus;
}

function gpsDisplayLabel(record) {
  const status = String(record?.gpsStatus || displayGpsStatus(record) || "").trim().toLowerCase();
  if (status === "stale") return "GPS Delayed";
  if (status === "offline") return "GPS Offline";
  if (status === "online") return "GPS Online";
  return status ? `GPS ${titleCase(status)}` : "GPS Unknown";
}

function gpsDisplayTone(record) {
  const status = String(record?.gpsStatus || displayGpsStatus(record) || "").trim().toLowerCase();
  if (status === "offline") return "critical";
  if (status === "stale") return "caution";
  if (status === "online") return "healthy";
  return "neutral";
}

function bookingVehicleId(booking) {
  const vehicle = booking?.vehicleId;
  return String(vehicle?._id || vehicle?.id || vehicle || "");
}

function isMonitoringEligibleBooking(booking) {
  return booking?.isArchived !== true &&
    MONITORING_BOOKING_STATUSES.has(String(booking?.status || "").trim().toLowerCase()) &&
    MONITORING_PAYMENT_STATUSES.has(String(booking?.paymentStatus || "").trim().toLowerCase());
}

function bookingForVehicle(bookings, record) {
  const vehicleId = String(record?.vehicle?.id || "");
  const activeId = String(record?.activeBooking?.id || "");
  if (!vehicleId || !activeId) return null;
  const booking = bookings.find((candidate) =>
    bookingVehicleId(candidate) === vehicleId &&
    String(candidate?._id || candidate?.id || "") === activeId &&
    isMonitoringEligibleBooking(candidate)) || null;
  const liveAlertsEnabled = record?.activeBooking?.gpsAlertConfiguration?.alertsEnabled;
  const bookingAlertsEnabled = booking?.gpsAlertConfiguration?.alertsEnabled;
  return booking && typeof bookingAlertsEnabled !== "boolean" &&
    typeof liveAlertsEnabled === "boolean"
    ? { ...booking, gpsAlertConfiguration: {
      ...(booking.gpsAlertConfiguration || {}), alertsEnabled: liveAlertsEnabled,
    } }
    : booking;
}

function retainedOperationalBooking(booking, record) {
  const activeId = String(record?.activeBooking?.id || "");
  return activeId && isMonitoringEligibleBooking(booking) &&
    bookingVehicleId(booking) === String(record?.vehicle?.id || "") &&
    String(booking?._id || booking?.id || "") === activeId ? booking : null;
}

function hasActiveRouteDeviation(record) {
  return Boolean(record?.monitoringBooking) &&
    record.monitoringBooking.gpsAlertConfiguration?.alertsEnabled !== false &&
    record?.routeDeviationAlert?.active === true &&
    record?.routeDeviationAlert?.type === "route_deviation";
}

export function monitoringState(record, nowMs = Date.now()) {
  const booking = record?.monitoringBooking;
  const tracking = booking?.tracking || {};
  const startMs = bookingBoundaryMs(booking, "start");
  const endMs = bookingBoundaryMs(booking, "end");
  const startedMs = new Date(tracking?.startedAt || 0).getTime();
  const manualOverride = tracking.isActive === true && Number.isFinite(startMs) && startMs > nowMs &&
    (!startedMs || startedMs < startMs);
  const configuredAlertsEnabled = booking?.gpsAlertConfiguration?.alertsEnabled !== false;
  const alerts = booking && configuredAlertsEnabled ? "Alerts enabled" : "Alerts disabled";
  const activeRouteDeviation = hasActiveRouteDeviation(record);
  const attention = activeRouteDeviation || record?.powerDisconnected === true ||
    ["stale", "offline"].includes(record?.gpsStatus) ||
    ["out_of_the_way", "custom_out_of_route"].includes(String(tracking?.status || tracking?.mode || ""));
  const completed = String(booking?.status || "").toLowerCase() === "completed" ||
    (Number.isFinite(endMs) && endMs > 0 && endMs < nowMs);
  const upcoming = Number.isFinite(startMs) && startMs > nowMs && !manualOverride;
  const active = tracking.isActive === true ||
    (Number.isFinite(startMs) && Number.isFinite(endMs) && startMs <= nowMs && endMs >= nowMs);
  if (completed) return { key: "completed", label: "Completed", alerts, manualOverride: false };
  if (attention) return { key: "attention", label: "Attention required", alerts, manualOverride };
  if (manualOverride) return { key: "override", label: "Started early", alerts, manualOverride: true };
  if (active) return { key: "active", label: "Active tracking", alerts, manualOverride: false };
  if (upcoming) return { key: "upcoming", label: "Upcoming booking", alerts, manualOverride: false };
  return { key: "ready", label: "Available", alerts, manualOverride: false };
}

export function countCurrentMonitoringAlerts(items = [], bookings = [], nowMs = Date.now()) {
  if (!Array.isArray(items) || !Array.isArray(bookings)) return 0;
  const affectedVehicles = new Set();
  for (const record of items) {
    if (record?.source !== "physical") continue;
    const vehicleId = String(record?.vehicle?.id || "");
    if (!vehicleId) continue;
    const state = monitoringState({
      ...record,
      monitoringBooking: bookingForVehicle(bookings, record, nowMs),
    }, nowMs);
    if (state.key === "attention") affectedVehicles.add(vehicleId);
  }
  return affectedVehicles.size;
}

function bookingCoordinate(value) {
  const latitude = Number(value?.latitude ?? value?.lat);
  const longitude = Number(value?.longitude ?? value?.lng);
  return validCoordinates({ latitude, longitude });
}

function usesDeliveryAsOperationalOrigin(booking = {}) {
  return booking?.withDriver === false &&
    String(booking?.vehicleHandoffOption || "").trim().toLowerCase() === "delivery";
}

function operationalOriginCoordinate(booking = {}) {
  return bookingCoordinate(
    usesDeliveryAsOperationalOrigin(booking)
      ? booking?.deliveryCoords
      : booking?.pickupCoords
  );
}

function providerRouteCoordinates(route, { approved = false } = {}) {
  if (approved && (String(route?.status || "").toLowerCase() !== "approved" ||
      route?.isCurrentForBooking === false)) return [];
  if (route?.geometry?.type !== "LineString" || !Array.isArray(route.geometry.coordinates)) return [];
  const coordinates = route.geometry.coordinates
    .map((pair) => bookingCoordinate({ lng: pair?.[0], lat: pair?.[1] })).filter(Boolean);
  return coordinates.length > 1 ? coordinates : [];
}

function previewRouteFromResponse(data, { allowSupportedProviders = false } = {}) {
  const result = data?.status === "available" ? data.result : null;
  const candidate = Array.isArray(result?.candidates) ? result.candidates[0] : null;
  const preview = result && candidate ? {
    provider: result.provider,
    geometry: candidate.geometry,
    origin: { coordinates: result.origin },
    destination: { coordinates: result.destination },
    previewOnly: data?.persistence?.status === "failed" &&
      data?.approval?.status === "not_approved" && data?.monitoringEligible === false,
  } : null;
  const provider = String(preview?.provider || "").toLowerCase();
  return (provider === "ors" ||
    (allowSupportedProviders && ["osrm", "google_routes"].includes(provider))) &&
    providerRouteCoordinates(preview).length > 1 ? preview : null;
}

function routeMonitorCoordinates(monitor) {
  if (!Array.isArray(monitor?.expectedRoute)) return [];
  const coordinates = monitor.expectedRoute.map(bookingCoordinate).filter(Boolean);
  return coordinates.length > 1 ? coordinates : [];
}

function routeSelectionKey(record) {
  const vehicleId = String(record?.vehicle?.id || "");
  const bookingId = String(record?.monitoringBooking?._id || record?.monitoringBooking?.id || "");
  return vehicleId && bookingId ? `${vehicleId}:${bookingId}` : "";
}

function hasRealRouteGeometry(operations) {
  return providerRouteCoordinates(operations?.plannedRoute, { approved: true }).length > 1 ||
    routeMonitorCoordinates(operations?.routeMonitor).length > 1 ||
    providerRouteCoordinates(operations?.routePreview).length > 1;
}

function emptySelectedOperations(routeKey = null) {
  return Object.freeze({
    routeSelectionKey: routeKey,
    routeLookupComplete: false,
    routeRequestInFlight: false,
    routeLookupState: "loading",
    routePreviewError: null,
    resolvedBookingId: null,
    plannedRoute: null,
    routeMonitor: null,
    routePreview: null,
    alertConfiguration: null,
    alertConfigurationLimits: null,
    alertConfigurationPreview: null,
  });
}

function selectedRoute(record, operations, historyLine = []) {
  if (!record) return { route: [], pickup: null, destination: null, source: "unavailable" };
  const booking = record?.monitoringBooking;
  if (!booking) return { route: [], pickup: null, destination: null, source: "unavailable" };
  const selectionKey = routeSelectionKey(record);
  if (operations?.routeSelectionKey !== selectionKey ||
      (!operations?.routeLookupComplete && !hasRealRouteGeometry(operations))) {
    return { route: [], pickup: operationalOriginCoordinate(booking),
      destination: bookingCoordinate(booking.dropoffCoords || booking.destinationCoords),
      source: "loading_route" };
  }
  if (operations?.routeLookupState === "error" && !hasRealRouteGeometry(operations) &&
      historyLine.length < 2) {
    return { route: [], pickup: operationalOriginCoordinate(booking),
      destination: bookingCoordinate(booking.dropoffCoords || booking.destinationCoords),
      source: "unavailable" };
  }
  const tracking = booking?.tracking || {};
  const monitoredCoordinates = routeMonitorCoordinates(operations?.routeMonitor);
  if (monitoredCoordinates.length > 1) {
    return {
      route: monitoredCoordinates,
      pickup: operationalOriginCoordinate(booking) || monitoredCoordinates[0],
      destination: bookingCoordinate(booking.dropoffCoords || booking.destinationCoords) || monitoredCoordinates.at(-1),
      source: "optimized_route",
      provider: operations.routeMonitor.routeSource || null,
    };
  }
  const approved = operations?.plannedRoute;
  const approvedCoordinates = providerRouteCoordinates(approved, { approved: true });
  if (approvedCoordinates.length > 1) {
    return {
      route: approvedCoordinates,
      pickup: operationalOriginCoordinate(booking) || bookingCoordinate(approved.origin?.coordinates) || approvedCoordinates[0],
      destination: bookingCoordinate(booking.dropoffCoords || booking.destinationCoords) ||
        bookingCoordinate(approved.destination?.coordinates) || approvedCoordinates.at(-1),
      source: "optimized_route",
      provider: String(approved.provider || "").toLowerCase() || null,
    };
  }
  if (historyLine.length > 1) {
    return {
      route: historyLine,
      pickup: historyLine[0],
      destination: historyLine.at(-1),
      source: "gps_history",
      provider: null,
    };
  }
  const preview = operations?.routePreview;
  const previewCoordinates = providerRouteCoordinates(preview);
  if (previewCoordinates.length > 1) {
    return {
      route: previewCoordinates,
      pickup: operationalOriginCoordinate(booking) || bookingCoordinate(preview.origin?.coordinates) || previewCoordinates[0],
      destination: bookingCoordinate(booking.dropoffCoords || booking.destinationCoords) ||
        bookingCoordinate(preview.destination?.coordinates) || previewCoordinates.at(-1),
      source: preview.previewOnly ? "preview_only" : "optimized_route",
      provider: String(preview.provider || "").toLowerCase() || null,
    };
  }
  const route = Array.isArray(tracking.route) ? tracking.route.map(bookingCoordinate).filter(Boolean) : [];
  const pickup = bookingCoordinate(tracking.pickupCoords) || operationalOriginCoordinate(booking);
  const destination = bookingCoordinate(tracking.dropoffCoords || booking?.dropoffCoords || booking?.destinationCoords);
  return { route: route.length > 1 ? route : [pickup, destination].filter(Boolean), pickup, destination,
    source: "estimated_route", provider: null };
}

function formatMeters(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? `${numeric.toLocaleString()} m` : "Not configured";
}

function formatSeconds(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return "Not configured";
  return numeric % 60 === 0 ? `${numeric / 60} min` : `${numeric} sec`;
}

function distanceKm(from, to) {
  const radians = (degrees) => degrees * Math.PI / 180;
  const latitudeDelta = radians(to.latitude - from.latitude);
  const longitudeDelta = radians(to.longitude - from.longitude);
  const a = Math.sin(latitudeDelta / 2) ** 2 + Math.cos(radians(from.latitude)) *
    Math.cos(radians(to.latitude)) * Math.sin(longitudeDelta / 2) ** 2;
  const safeA = Math.min(1, Math.max(0, a));
  return 6371 * 2 * Math.atan2(Math.sqrt(safeA), Math.sqrt(1 - safeA));
}

function bearingDegrees(from, to) {
  const radians = (degrees) => degrees * Math.PI / 180;
  const degrees = (value) => value * 180 / Math.PI;
  const fromLatitude = radians(from.latitude);
  const toLatitude = radians(to.latitude);
  const longitudeDelta = radians(to.longitude - from.longitude);
  const y = Math.sin(longitudeDelta) * Math.cos(toLatitude);
  const x = Math.cos(fromLatitude) * Math.sin(toLatitude) -
    Math.sin(fromLatitude) * Math.cos(toLatitude) * Math.cos(longitudeDelta);
  return (degrees(Math.atan2(y, x)) + 360) % 360;
}

function routeContext(route, current, speedKph) {
  if (route.length < 2) return { direction: null, eta: "Not available", remaining: null };
  let closestIndex = 0;
  if (current) {
    route.forEach((point, index) => {
      if (distanceKm(current, point) < distanceKm(current, route[closestIndex])) closestIndex = index;
    });
  }
  const segmentIndex = Math.min(closestIndex, route.length - 2);
  const from = route[segmentIndex];
  const to = route[segmentIndex + 1];
  const direction = {
    coordinates: {
      latitude: (from.latitude + to.latitude) / 2,
      longitude: (from.longitude + to.longitude) / 2,
    },
    bearing: bearingDegrees(from, to),
  };
  let remaining = current ? distanceKm(current, route[closestIndex]) : 0;
  for (let index = closestIndex; index < route.length - 1; index += 1) {
    remaining += distanceKm(route[index], route[index + 1]);
  }
  const etaMinutes = Number.isFinite(speedKph) && speedKph >= 5
    ? Math.max(1, Math.round(remaining / speedKph * 60))
    : null;
  return {
    direction,
    eta: etaMinutes ? `${etaMinutes} min` : "Awaiting movement",
    remaining,
  };
}

function vehicleImageUrl(vehicle) {
  const value = String(vehicle?.image || "").trim();
  if (!value) return null;
  try {
    const url = new URL(value, window.location.origin);
    return ["http:", "https:"].includes(url.protocol) ? url.href : null;
  } catch {
    return null;
  }
}

function escapeImageAttribute(value) {
  return value.replace(/[&"'<>]/g, (character) => ({
    "&": "&amp;", "\"": "&quot;", "'": "&#39;", "<": "&lt;", ">": "&gt;",
  })[character]);
}

function VehicleAvatar({ vehicle, className }) {
  const image = vehicleImageUrl(vehicle);
  const [failedImage, setFailedImage] = useState(null);
  return <span className={className}>{image && failedImage !== image
    ? <img key={image} src={image} alt="" onError={() => setFailedImage(image)} />
    : vehicle?.make?.charAt(0) || "V"}</span>;
}

function markerIcon(record, selected, severity = "normal") {
  const image = vehicleImageUrl(record?.vehicle);
  const heading = Number.isFinite(record?.headingDegrees) ? record.headingDegrees : 0;
  const alert = severity !== "normal";
  return L.divIcon({
    className: `gps-fleet-marker gps-fleet-marker--physical gps-fleet-marker--${severity} ${selected ? "is-selected" : ""} ${alert ? "is-alert" : ""}`,
    html: image
      ? `<div class="gps-fleet-marker-photo"><i style="transform:rotate(${heading}deg)">▲</i><img src="${escapeImageAttribute(image)}" alt="" /></div>`
      : `<span style="transform:rotate(${heading}deg)">▲</span>`,
    iconSize: [42, 42],
    iconAnchor: [21, 21],
  });
}

export function nearestPointOnRoute(point, route = []) {
  const current = validCoordinates(point);
  if (!current || route.length < 2) return null;
  const earthRadiusMeters = 6371000;
  const referenceLatitude = current.latitude * Math.PI / 180;
  const project = (coordinate) => ({
    x: coordinate.longitude * Math.PI / 180 * earthRadiusMeters * Math.cos(referenceLatitude),
    y: coordinate.latitude * Math.PI / 180 * earthRadiusMeters,
  });
  const projectedCurrent = project(current);
  let nearest = null;
  let nearestSquaredDistance = Number.POSITIVE_INFINITY;
  for (let index = 0; index < route.length - 1; index += 1) {
    const start = validCoordinates(route[index]);
    const end = validCoordinates(route[index + 1]);
    if (!start || !end) continue;
    const a = project(start);
    const b = project(end);
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const lengthSquared = dx * dx + dy * dy;
    const projection = lengthSquared > 0
      ? ((projectedCurrent.x - a.x) * dx + (projectedCurrent.y - a.y) * dy) / lengthSquared
      : 0;
    const ratio = Math.max(0, Math.min(1, projection));
    const x = a.x + ratio * dx;
    const y = a.y + ratio * dy;
    const squaredDistance = (projectedCurrent.x - x) ** 2 + (projectedCurrent.y - y) ** 2;
    if (squaredDistance < nearestSquaredDistance) {
      nearestSquaredDistance = squaredDistance;
      nearest = {
        latitude: start.latitude + ratio * (end.latitude - start.latitude),
        longitude: start.longitude + ratio * (end.longitude - start.longitude),
      };
    }
  }
  return nearest;
}

function directionIcon(bearing) {
  return L.divIcon({
    className: "gps-fleet-route-direction",
    html: `<span style="transform:rotate(${bearing}deg)">↑</span>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });
}

function MapBounds({ focusKey, record, route, history, corridor = [], records = [], fleetOverview = false }) {
  const map = useMap();
  const lastFittedFocus = useRef(null);
  useEffect(() => {
    if (!focusKey || lastFittedFocus.current === focusKey) return;
    const points = [
      validCoordinates(record?.coordinates),
      ...records.map((item) => validCoordinates(item?.coordinates)),
      ...(Array.isArray(route) ? route : []),
      ...(Array.isArray(corridor) ? corridor : []),
      ...(Array.isArray(history) ? history : []).map((position) => validCoordinates(position?.coordinates)),
    ].filter(Boolean).map((point) => [point.latitude, point.longitude]);
    if (points.length > 1) {
      map.fitBounds(points, { padding: [36, 36], maxZoom: fleetOverview ? 12 : 16 });
      lastFittedFocus.current = focusKey;
    } else if (points.length === 1) {
      map.setView(points[0], fleetOverview ? 10 : 14);
      lastFittedFocus.current = focusKey;
    }
  }, [corridor, fleetOverview, focusKey, history, map, record, records, route]);
  return null;
}

function MapVehicleFocus({ request, selectedVehicleId, previewVisible }) {
  const map = useMap();
  const lastHandledRequest = useRef(0);
  useEffect(() => {
    if (!request || request.id === lastHandledRequest.current ||
        request.vehicleId !== selectedVehicleId) return;
    lastHandledRequest.current = request.id;
    if (previewVisible) return;
    const position = leafletPosition(request.coordinates);
    if (position) map.flyTo(position, 14, { animate: true, duration: 0.8 });
  }, [map, previewVisible, request, selectedVehicleId]);
  return null;
}

export function useSafePolling(callback, activeMs, enabled) {
  const callbackRef = useRef(callback);
  useEffect(() => { callbackRef.current = callback; }, [callback]);
  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    let timer = null;
    const schedule = () => {
      if (cancelled) return;
      const delay = document.visibilityState === "hidden" ? HIDDEN_POLL_MS : activeMs;
      timer = window.setTimeout(async () => {
        await callbackRef.current({ silent: true });
        schedule();
      }, delay);
    };
    schedule();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [activeMs, enabled]);
}

function DetailRow({ label, value, tone = null }) {
  return <div className="gps-fleet-detail-row"><span>{label}</span>
    <strong className={tone ? `gps-fleet-detail-value--${tone}` : undefined}>{value}</strong></div>;
}

function FleetSummaryIcon({ tone }) {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {tone === "upcoming" ? <>
      <rect x="3.5" y="5" width="17" height="15" rx="2" />
      <path d="M7 3v4M17 3v4M3.5 9h17M8 14h2M14 14h2" />
    </> : tone === "attention" ? <>
      <path d="M10.3 4.2a2 2 0 0 1 3.4 0l8 13.3a2 2 0 0 1-1.7 3H4a2 2 0 0 1-1.7-3z" />
      <path d="M12 9v5M12 17h.01" />
    </> : <>
      <path d="m5 10 1.6-4.2A2 2 0 0 1 8.5 4.5h7a2 2 0 0 1 1.9 1.3L19 10" />
      <path d="M4 10h16a1 1 0 0 1 1 1v6H3v-6a1 1 0 0 1 1-1ZM5 17v2M19 17v2M7 13h2M15 13h2" />
    </>}
  </svg>;
}

function FleetMap({ selected, allVehicles = [], history, operations, upcomingPreview = null,
  alertControls = null, mapSectionRef = null, vehicleFocus = null, onRouteRetry = null,
  fleetFitRequest = 0, onSelectVehicle = () => {} }) {
  const current = validCoordinates(selected?.coordinates);
  const historyLine = (Array.isArray(history) ? history : [])
    .map((position) => validCoordinates(position?.coordinates)).filter(Boolean);
  const previewVisible = Array.isArray(upcomingPreview?.route) && upcomingPreview.route.length > 1;
  const bookingRoute = previewVisible
    ? { route: upcomingPreview.route, pickup: upcomingPreview.pickup,
      destination: upcomingPreview.destination, source: "upcoming_preview", provider: upcomingPreview.provider }
    : selectedRoute(selected, operations, historyLine);
  const routeLine = Array.isArray(bookingRoute.route)
    ? bookingRoute.route.map(validCoordinates).filter(Boolean)
    : [];
  const routeSourceLabel = {
    optimized_route: "Optimized route",
    gps_history: "GPS history",
    estimated_route: "Estimated route",
    loading_route: "Loading route",
    preview_only: "Route preview available",
    upcoming_preview: "Upcoming route",
  }[bookingRoute.source] || "Unavailable";
  const previewOnlyRoute = !previewVisible && bookingRoute.source === "preview_only";
  const reportedSeverity = getMonitoringSeverity(selected);
  const severity = previewOnlyRoute && reportedSeverity.routeDeviation
    ? { ...reportedSeverity, routeDeviation: false,
      key: reportedSeverity.gpsOffline ? "critical" : reportedSeverity.powerDisconnected ? "power" : "normal",
      label: reportedSeverity.gpsOffline ? "GPS Offline"
        : reportedSeverity.powerDisconnected ? "Power Disconnected" : null }
    : reportedSeverity;
  const severityActive = severity.key !== "normal";
  const activeDeviationAlert = !previewVisible && !previewOnlyRoute && hasActiveRouteDeviation(selected);
  const routeStatus = routeContext(routeLine, current, Number(selected?.speedKph));
  const alertConfiguration = operations?.alertConfigurationPreview || operations?.alertConfiguration ||
    selected?.monitoringBooking?.gpsAlertConfiguration ||
    (selected?.monitoringBooking ? DEFAULT_BOOKING_ALERT_CONFIGURATION : null);
  const alertColor = { strict: "#64748b", normal: "#64748b", relaxed: "#16a34a" }[
    alertConfiguration?.mode
  ] || "#d97706";
  const deviationRadiusMeters = Number(alertConfiguration?.deviationRadiusMeters);
  const deviationCorridor = previewVisible || previewOnlyRoute ? [] : routeDeviationCorridor(routeLine, deviationRadiusMeters);
  const expectedRoutePoint = activeDeviationAlert ? nearestPointOnRoute(current, routeLine) : null;
  const currentPosition = leafletPosition(current);
  const routePositions = leafletPath(routeLine);
  const historyPositions = leafletPath(historyLine);
  const corridorPositions = leafletPath(deviationCorridor, 3);
  const pickupPosition = leafletPosition(bookingRoute.pickup);
  const destinationPosition = leafletPosition(bookingRoute.destination);
  const expectedRoutePosition = leafletPosition(expectedRoutePoint);
  const directionPosition = previewVisible ? null : leafletPosition(routeStatus.direction?.coordinates);
  const missingOperationalOrigin = Boolean(
    selected?.monitoringBooking &&
    !operationalOriginCoordinate(selected.monitoringBooking)
  );
  const missingOriginMessage = usesDeliveryAsOperationalOrigin(selected?.monitoringBooking)
    ? "Route unavailable: delivery coordinates are missing."
    : "Route unavailable: pickup coordinates are missing.";
  const deviationPositions = expectedRoutePosition && currentPosition
    ? [expectedRoutePosition, currentPosition]
    : [];
  const distanceOutside = Number(selected?.routeDeviationAlert?.distanceOutsideAllowedRadiusMeters);
  const alertDistanceLabel = Number.isFinite(distanceOutside)
    ? `${formatMeters(distanceOutside)} outside allowed radius`
    : "Outside allowed route radius";
  const corridorColor = activeDeviationAlert ? "#e66a1f" : alertColor;
  const alertSummary = severity.gpsOffline
    ? "GPS telemetry is not currently fresh."
    : severity.powerDisconnected
      ? severity.routeDeviation ? `${alertDistanceLabel}; tracker power is disconnected.` : "Tracker power is disconnected."
      : alertDistanceLabel;
  const alertDetectedAt = severity.powerDisconnected
    ? selected?.powerCutAlert?.detectedAt
    : activeDeviationAlert ? selected?.routeDeviationAlert?.detectedAt : null;
  const allVehiclesFocusKey = allVehicles.map((item) => String(item.vehicle?.id || "")).join("|");
  const mapFocusKey = selected ? [
    selected.vehicle?.id,
    previewVisible ? upcomingPreview.bookingId :
      selected.monitoringBooking?._id || selected.monitoringBooking?.id || "no-booking",
  ].join(":") : `all:${allVehiclesFocusKey}:${fleetFitRequest}`;
  return (
    <section ref={mapSectionRef} className={`gps-fleet-map-card gps-fleet-map-card--${severity.key}`} aria-label="Fleet map" data-route-source={bookingRoute.source}
      data-route-provider={bookingRoute.provider || "none"}
      data-gps-tone={gpsDisplayTone(selected)}
      data-alert-mode={alertConfiguration?.mode || "none"}>
      <header className="gps-fleet-map-head">
        {selected ? <>
          <div className="gps-fleet-map-vehicle">
            <VehicleAvatar vehicle={selected.vehicle} className="gps-fleet-vehicle-avatar" />
            <div><strong>{[selected.vehicle?.make, selected.vehicle?.model].filter(Boolean).join(" ") || "Selected vehicle"}</strong>
              <small>{selected.vehicle?.plateNumber || NOT_AVAILABLE}</small></div>
          </div>
          <div className="gps-fleet-map-context"><span>Booking reference</span>
            <strong>{selected.monitoringBooking?.bookingReference ||
              selected.activeBooking?.reference || NOT_AVAILABLE}</strong></div>
          <div className="gps-fleet-map-context"><span>Rental state</span>
            <strong className={`gps-fleet-header-status gps-fleet-header-status--${selected.activeBooking
              ? "active" : selected.upcomingBooking ? "upcoming" : "neutral"}`}>
              {selected.activeBooking ? "Active booking" :
                selected.upcomingBooking ? "Upcoming booking" : "No active booking"}</strong></div>
          <div className="gps-fleet-map-context"><span>Tracking status</span>
            <strong className={`gps-fleet-header-status gps-fleet-header-status--${gpsDisplayTone(selected)}`}>
              {gpsDisplayLabel(selected)}</strong></div>
          {alertControls}
        </> : <div><strong>All vehicles</strong><small>Showing {allVehicles.length} GPS-enabled vehicles with valid locations.</small></div>}
      </header>
      {severityActive ? <div className={`gps-fleet-deviation-banner gps-fleet-deviation-banner--${severity.key}`} role="status"
        aria-label="Current monitoring alert summary">
        <span className="gps-fleet-deviation-banner-icon" aria-hidden="true">!</span>
        <strong>{severity.gpsOffline
          ? gpsDisplayTone(selected) === "caution" ? "Telemetry delayed" : "Tracking issue"
          : severity.label}</strong>
        <span>{alertSummary}</span>
        {alertDetectedAt ? <time dateTime={alertDetectedAt}>
          {formatTime(alertDetectedAt)}
        </time> : null}
      </div> : null}
      <MapContainer center={[12.8797, 121.774]} zoom={5} className="gps-fleet-map">
        <MapBounds focusKey={`${mapFocusKey}:${bookingRoute.source}`}
          record={selected} records={selected ? EMPTY_FLEET_RECORDS : allVehicles} route={routeLine}
          history={previewVisible ? [] : history} corridor={deviationCorridor} fleetOverview={!selected} />
        <MapVehicleFocus request={vehicleFocus} selectedVehicleId={String(selected?.vehicle?.id || "")}
          previewVisible={previewVisible} />
        <TileLayer attribution="&copy; OpenStreetMap contributors" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        {corridorPositions.length > 2 ? <>
          <Polygon positions={corridorPositions}
            pathOptions={{ color: corridorColor, weight: activeDeviationAlert ? 3 : 2,
              fillColor: corridorColor, fillOpacity: activeDeviationAlert ? 0.17 : 0.14 }} />
        </> : null}
        {routePositions.length > 1 ? <>
          <Polyline positions={routePositions}
            pathOptions={{ color: "#0f172a", weight: 11, opacity: 0.25, lineCap: "round", lineJoin: "round" }} />
          <Polyline positions={routePositions}
            pathOptions={{ color: bookingRoute.source === "gps_history" ? "#06b6d4" : "#2563eb",
              weight: 7, opacity: 0.96, lineCap: "round", lineJoin: "round" }} />
        </> : null}
        {!previewVisible && historyPositions.length > 1 && bookingRoute.source !== "gps_history" ? <>
          <Polyline positions={historyPositions}
            pathOptions={{ color: "#0f172a", weight: 9, opacity: 0.2, lineCap: "round", lineJoin: "round" }} />
          <Polyline positions={historyPositions}
            pathOptions={{ color: "#06b6d4", weight: 5, opacity: 0.95, lineCap: "round", lineJoin: "round" }} />
        </> : null}
        {activeDeviationAlert && deviationPositions.length === 2 ? <Polyline
          positions={deviationPositions}
          pathOptions={{ color: "#e66a1f", weight: 3, opacity: 0.95, dashArray: "7 7" }} /> : null}
        {pickupPosition ? <CircleMarker center={pickupPosition}
          radius={9} pathOptions={{ color: "#fff", weight: 4, fillColor: "#16a34a", fillOpacity: 1 }}>
          <Tooltip permanent direction="top" offset={[0, -10]}>Pickup</Tooltip>
        </CircleMarker> : null}
        {destinationPosition ? <CircleMarker center={destinationPosition}
          radius={9} pathOptions={{ color: "#fff", weight: 4, fillColor: "#ef4444", fillOpacity: 1 }}>
          <Tooltip permanent direction="top" offset={[0, -10]}>Drop-off</Tooltip>
        </CircleMarker> : null}
        {currentPosition ? <Marker key={`vehicle-${currentPosition.join("-")}-${severity.key}`}
          position={currentPosition}
          icon={markerIcon(selected, true, severity.key)}>
          <Tooltip permanent direction="top" offset={[0, -24]}>
            {[selected.vehicle?.make, selected.vehicle?.model, selected.vehicle?.plateNumber]
              .filter(Boolean).join(" ") || "Current vehicle location"}
          </Tooltip>
        </Marker> : null}
        {!selected ? allVehicles.map((record) => {
          const position = leafletPosition(record.coordinates);
          if (!position) return null;
          return <Marker key={`fleet-vehicle-${record.vehicle?.id}`} position={position}
            icon={markerIcon(record, false, getMonitoringSeverity(record).key)}
            eventHandlers={{ click: () => onSelectVehicle(record, { focusMap: true }) }}>
            <Tooltip>{[record.vehicle?.make, record.vehicle?.model, record.vehicle?.plateNumber]
              .filter(Boolean).join(" ") || "Vehicle location"}</Tooltip>
          </Marker>;
        }) : null}
        {directionPosition && Number.isFinite(routeStatus.direction?.bearing) ? <Marker
          position={directionPosition}
          icon={directionIcon(routeStatus.direction.bearing)} interactive={false}>
          <Tooltip>Route direction</Tooltip>
        </Marker> : null}
      </MapContainer>
      {selected && !current ? <div className="gps-fleet-map-empty">This vehicle does not have a current GPS position.</div> : null}
      {selected ? <div className="gps-fleet-map-footer">
        <div className="gps-fleet-route-source" aria-label="Route status">
        <small>{!previewVisible && missingOperationalOrigin ? "Route unavailable" : "Route source"}</small>
        <strong>{!previewVisible && missingOperationalOrigin ? missingOriginMessage : bookingRoute.source === "loading_route"
          ? routeSourceLabel : routeLine.length > 1 ? routeSourceLabel : "Route unavailable"}</strong>
        {previewOnlyRoute ? <small>Monitoring setup unavailable</small> : null}
        {operations?.routePreviewError ? <small role="status">{routeLine.length > 1
          ? "Route refresh unavailable" : "Route unavailable"}</small> : null}
        {onRouteRetry && (operations?.routePreviewError || routeLine.length > 1 &&
          !previewVisible && bookingRoute.source !== "gps_history") ? <button type="button"
          onClick={onRouteRetry} disabled={operations?.routeRequestInFlight}>
          {operations?.routePreviewError ? "Retry" : "Refresh route"}
        </button> : null}
        </div>
        <div className={`gps-fleet-map-live ${severityActive ? `is-alert gps-fleet-map-live--${severity.key}` : ""}`}><i />
        Last GPS update
          <span>{formatTime(selected.recordedAt)}</span>
        </div>
      </div> : null}
    </section>
  );
}

export default function GpsFleetDashboard({ activeView = "fleet", onViewChange = () => {} }) {
  const { user } = useAuth() || {};
  const role = String(user?.role || "").trim().toLowerCase().replace(/[ -]+/g, "_");
  const diagnosticTelemetryEnabled = role === "technical_admin";
  const [fleet, setFleet] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [monitoringPolicy, setMonitoringPolicy] = useState(Object.freeze({}));
  const [selected, setSelected] = useState(null);
  const [fleetFitRequest, setFleetFitRequest] = useState(0);
  const [selectedOperations, setSelectedOperations] = useState(() => emptySelectedOperations());
  const [upcomingPreview, setUpcomingPreview] = useState(null);
  const [vehicleFocus, setVehicleFocus] = useState(null);
  const [routeRefreshToken, setRouteRefreshToken] = useState(0);
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [historyCursor, setHistoryCursor] = useState(null);
  const [historyHasMore, setHistoryHasMore] = useState(false);
  const [historyQuery, setHistoryQuery] = useState(null);
  const [historyFrom, setHistoryFrom] = useState("");
  const [historyTo, setHistoryTo] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const fleetRequest = useRef(null);
  const bookingsRequest = useRef(null);
  const selectedRequest = useRef(null);
  const operationsRequest = useRef(null);
  const operationsRequestSequence = useRef(0);
  const upcomingPreviewRequest = useRef(null);
  const upcomingPreviewSequence = useRef(0);
  const upcomingPreviewIntent = useRef(null);
  const mapSectionRef = useRef(null);
  const vehicleFocusSequence = useRef(0);
  const lastGoodRoute = useRef(null);
  const historyRequest = useRef(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      fleetRequest.current?.abort();
      bookingsRequest.current?.abort();
      selectedRequest.current?.abort();
      operationsRequest.current?.abort();
      upcomingPreviewRequest.current?.abort();
      historyRequest.current?.abort();
    };
  }, []);

  const loadFleet = useCallback(async ({ silent = false } = {}) => {
    if (fleetRequest.current) return;
    const controller = new AbortController();
    fleetRequest.current = controller;
    if (!silent) setRefreshing(true);
    try {
      const response = await api.get("/gps/fleet/live", {
        params: { page: 1, limit: 1000, source: "physical" }, signal: controller.signal,
      });
      if (!mounted.current) return;
      const physicalItems = (response.data?.items || []).filter((item) => item?.source === "physical");
      setFleet({ ...response.data, items: physicalItems });
      setError("");
      setDisabled(false);
      setSelected((current) => {
        if (!current) return current;
        const refreshed = physicalItems.find((item) => item.vehicle?.id === current.vehicle?.id);
        return refreshed ? {
          ...refreshed,
          monitoringBooking: retainedOperationalBooking(current.monitoringBooking, refreshed),
        } : null;
      });
    } catch (caught) {
      if (!mounted.current || caught?.code === "ERR_CANCELED") return;
      if (caught?.response?.status === 404) {
        setDisabled(true);
        setError("");
      } else {
        setError(GPS_UNAVAILABLE_MESSAGE);
      }
    } finally {
      fleetRequest.current = null;
      if (mounted.current) { setLoading(false); setRefreshing(false); }
    }
  }, []);

  const initialize = useCallback(async () => {
    try {
      const availability = await api.get("/gps/availability");
      if (mounted.current) setMonitoringPolicy(availability.data?.monitoring || Object.freeze({}));
      await loadFleet();
    } catch (caught) {
      if (!mounted.current) return;
      setLoading(false);
      if (caught?.response?.status === 404) setDisabled(true);
      else setError(GPS_UNAVAILABLE_MESSAGE);
    }
  }, [loadFleet]);

  const loadBookings = useCallback(async () => {
    if (bookingsRequest.current) return;
    const controller = new AbortController();
    bookingsRequest.current = controller;
    let cancelled = false;
    try {
      const response = await api.get("/bookings", { params: { limit: 100 }, signal: controller.signal });
      if (mounted.current && Array.isArray(response.data?.bookings)) setBookings(response.data.bookings);
    } catch (caught) {
      cancelled = controller.signal.aborted || caught?.code === "ERR_CANCELED" ||
        ["AbortError", "CanceledError"].includes(caught?.name);
    } finally {
      if (bookingsRequest.current === controller) bookingsRequest.current = null;
      if (cancelled && mounted.current) {
        window.setTimeout(() => { if (mounted.current) loadBookings(); }, 0);
      }
    }
  }, []);

  useEffect(() => { initialize(); loadBookings(); }, [initialize, loadBookings]);
  useSafePolling(loadFleet, FLEET_POLL_MS, !loading && !disabled);
  useSafePolling(loadBookings, 30000, !loading && !disabled);

  const loadSelected = useCallback(async ({ silent = false } = {}) => {
    if (!selected?.vehicle?.id || selectedRequest.current) return;
    const controller = new AbortController();
    selectedRequest.current = controller;
    try {
      const response = await api.get(`/gps/vehicles/${selected.vehicle.id}/location`, {
        params: { source: "physical" }, signal: controller.signal,
      });
      if (mounted.current) {
        setSelected((current) => {
          if (!current || String(current.vehicle?.id || "") !==
              String(response.data?.vehicle?.id || "")) return current;
          return response.data?.source === "physical"
            ? { ...response.data,
              activeBooking: current.activeBooking || null,
              monitoringBooking: retainedOperationalBooking(current.monitoringBooking, current) }
            : null;
        });
      }
    } catch (caught) {
      if (!silent && mounted.current && caught?.code !== "ERR_CANCELED") setError(GPS_UNAVAILABLE_MESSAGE);
    } finally {
      selectedRequest.current = null;
    }
  }, [selected?.vehicle?.id]);

  useSafePolling(loadSelected, SELECTED_POLL_MS, Boolean(selected?.vehicle?.id) && !disabled);

  const selectedVehicleId = String(selected?.vehicle?.id || "");
  const selectedBookingId = String(selected?.monitoringBooking?._id || selected?.monitoringBooking?.id || "");
  const selectedRouteSelectionKey = routeSelectionKey(selected);
  const currentSelectedOperations = selectedRouteSelectionKey &&
    selectedOperations.routeSelectionKey === selectedRouteSelectionKey
    ? selectedOperations : emptySelectedOperations();
  const currentUpcomingPreview = selectedVehicleId === upcomingPreview?.vehicleId &&
    String(selected?.upcomingBooking?.id || "") === upcomingPreview?.bookingId
    ? upcomingPreview
    : null;
  const selectedHasPreviewEndpoints = Boolean(
    operationalOriginCoordinate(selected?.monitoringBooking) &&
    bookingCoordinate(selected?.monitoringBooking?.destinationCoords)
  );
  useEffect(() => {
    operationsRequest.current?.abort();
    const requestSequence = ++operationsRequestSequence.current;
    if (!selectedRouteSelectionKey) {
      lastGoodRoute.current = null;
      setSelectedOperations(emptySelectedOperations());
      return undefined;
    }
    if (lastGoodRoute.current?.routeSelectionKey !== selectedRouteSelectionKey) {
      lastGoodRoute.current = null;
    }
    setSelectedOperations((current) => current.routeSelectionKey === selectedRouteSelectionKey
      ? Object.freeze({ ...current, routeRequestInFlight: true })
      : Object.freeze({ ...emptySelectedOperations(selectedRouteSelectionKey), routeRequestInFlight: true }));
    const controller = new AbortController();
    operationsRequest.current = controller;
    (async () => {
      const [routeResult, monitorResult, alertConfigurationResult] = await Promise.allSettled([
        api.get(`/gps/bookings/${selectedBookingId}/planned-routes/active`, { signal: controller.signal }),
        api.get(`/gps/bookings/${selectedBookingId}/route-monitor`, { signal: controller.signal }),
        api.get(`/gps/bookings/${selectedBookingId}/alert-configuration`, { signal: controller.signal }),
      ]);
      if (!mounted.current || controller.signal.aborted ||
          operationsRequestSequence.current !== requestSequence) return;
      const plannedRoute = routeResult.status === "fulfilled" ? routeResult.value.data?.route || null : null;
      const routeMonitor = monitorResult.status === "fulfilled" ? monitorResult.value.data?.monitor || null : null;
      let routePreview = null;
      let previewFailed = false;
      let previewAttempted = false;
      if (providerRouteCoordinates(plannedRoute, { approved: true }).length < 2 &&
          routeMonitorCoordinates(routeMonitor).length < 2 && selectedHasPreviewEndpoints &&
          upcomingPreviewIntent.current?.vehicleId !== selectedVehicleId) {
        previewAttempted = true;
        try {
          const previewResponse = await api.get(
            `/gps/bookings/${selectedBookingId}/planned-route-preview`,
            { params: { alternatives: 1 }, signal: controller.signal }
          );
          routePreview = previewRouteFromResponse(previewResponse.data);
        } catch {
          routePreview = null;
          previewFailed = true;
        }
      }
      if (!mounted.current || controller.signal.aborted ||
          operationsRequestSequence.current !== requestSequence) return;
      const candidateRoute = { plannedRoute, routeMonitor, routePreview };
      const hasCandidateRoute = hasRealRouteGeometry(candidateRoute);
      const lookupFailed = !hasCandidateRoute && (routeResult.status === "rejected" ||
        monitorResult.status === "rejected" || previewFailed || previewAttempted && !routePreview);
      if (hasCandidateRoute) {
        lastGoodRoute.current = Object.freeze({
          routeSelectionKey: selectedRouteSelectionKey,
          ...candidateRoute,
        });
      }
      const retainedRoute = !hasCandidateRoute &&
        lastGoodRoute.current?.routeSelectionKey === selectedRouteSelectionKey
        ? lastGoodRoute.current
        : null;
      const alertConfiguration = alertConfigurationResult.status === "fulfilled"
        ? alertConfigurationResult.value.data?.configuration || null : null;
      const alertConfigurationLimits = alertConfigurationResult.status === "fulfilled"
        ? alertConfigurationResult.value.data?.limits || null : null;
      setSelectedOperations((current) => {
        if (operationsRequestSequence.current !== requestSequence) return current;
        const currentMatches = current.routeSelectionKey === selectedRouteSelectionKey;
        const currentRoute = currentMatches && hasRealRouteGeometry(current) ? current : retainedRoute;
        const routeValues = hasCandidateRoute ? candidateRoute : currentRoute ? {
          plannedRoute: currentRoute.plannedRoute,
          routeMonitor: currentRoute.routeMonitor,
          routePreview: currentRoute.routePreview,
        } : candidateRoute;
        return Object.freeze({
          routeSelectionKey: selectedRouteSelectionKey,
          routeLookupComplete: true,
          routeRequestInFlight: false,
          routeLookupState: lookupFailed ? "error" : routePreview?.previewOnly
            ? "preview_only" : hasCandidateRoute || currentRoute ? "available" : "unavailable",
          routePreviewError: lookupFailed ? "route_unavailable" : null,
          resolvedBookingId: selectedBookingId,
          ...routeValues,
          alertConfiguration: alertConfiguration || (currentMatches ? current.alertConfiguration : null),
          alertConfigurationLimits: alertConfigurationLimits ||
            (currentMatches ? current.alertConfigurationLimits : null),
          alertConfigurationPreview: null,
        });
      });
      if (alertConfiguration) {
        setBookings((current) => current.map((booking) =>
          String(booking?._id || booking?.id || "") === selectedBookingId
            ? { ...booking, gpsAlertConfiguration: alertConfiguration }
            : booking
        ));
        setSelected((current) => {
          const booking = current?.monitoringBooking;
          return routeSelectionKey(current) === selectedRouteSelectionKey &&
            String(booking?._id || booking?.id || "") === selectedBookingId
            ? { ...current, monitoringBooking: { ...booking,
              gpsAlertConfiguration: alertConfiguration } }
            : current;
        });
      }
      if (operationsRequest.current === controller) operationsRequest.current = null;
    })();
    return () => {
      controller.abort();
      if (operationsRequest.current === controller) operationsRequest.current = null;
    };
  }, [routeRefreshToken, selectedBookingId, selectedHasPreviewEndpoints, selectedRouteSelectionKey, selectedVehicleId]);

  const refreshSelectedRoute = useCallback(() => {
    if (operationsRequest.current) return;
    setRouteRefreshToken((current) => current + 1);
  }, []);
  const retrySelectedRoute = useCallback(() => {
    if (operationsRequest.current || !selectedRouteSelectionKey) return;
    setSelectedOperations((current) => current.routeSelectionKey === selectedRouteSelectionKey
      ? Object.freeze({ ...current, routePreviewError: null, routeRequestInFlight: true })
      : current);
    setRouteRefreshToken((current) => current + 1);
  }, [selectedRouteSelectionKey]);
  useSafePolling(
    refreshSelectedRoute,
    30000,
    Boolean(selectedRouteSelectionKey) &&
      selectedHasPreviewEndpoints &&
      !hasRealRouteGeometry(currentSelectedOperations) &&
      !disabled
  );

  const clearUpcomingPreview = useCallback(() => {
    upcomingPreviewIntent.current = null;
    upcomingPreviewRequest.current?.abort();
    upcomingPreviewRequest.current = null;
    upcomingPreviewSequence.current += 1;
    setUpcomingPreview(null);
  }, []);

  const selectAllVehicles = useCallback(() => {
    setFleetFitRequest((request) => request + 1);
    setVehicleFocus(null);
    selectedRequest.current?.abort();
    selectedRequest.current = null;
    historyRequest.current?.abort();
    historyRequest.current = null;
    operationsRequest.current?.abort();
    operationsRequest.current = null;
    operationsRequestSequence.current += 1;
    lastGoodRoute.current = null;
    clearUpcomingPreview();
    setSelected(null);
    setSelectedOperations(emptySelectedOperations());
    setHistory([]);
    setHistoryCursor(null);
    setHistoryHasMore(false);
    setHistoryQuery(null);
    setHistoryOpen(false);
    setHistoryError("");
  }, [clearUpcomingPreview]);

  const selectVehicle = useCallback((record, { focusMap = false } = {}) => {
    if (record?.source !== "physical") return;
    if (focusMap) {
      setVehicleFocus({
        id: ++vehicleFocusSequence.current,
        vehicleId: String(record.vehicle?.id || ""),
        coordinates: validCoordinates(record.coordinates),
      });
      const scrollToMap = () => {
        mapSectionRef.current?.scrollIntoView?.({
          behavior: window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ? "auto" : "smooth",
          block: "center",
        });
      };
      if (typeof window.requestAnimationFrame === "function") window.requestAnimationFrame(scrollToMap);
      else scrollToMap();
    }
    const nextSelectionKey = routeSelectionKey(record);
    const sameSelection = String(record?.vehicle?.id || "") === selectedVehicleId &&
      nextSelectionKey === selectedRouteSelectionKey;
    selectedRequest.current?.abort();
    selectedRequest.current = null;
    if (sameSelection) {
      setSelected(record);
      return;
    }
    if (String(record?.vehicle?.id || "") !== selectedVehicleId) clearUpcomingPreview();
    historyRequest.current?.abort();
    operationsRequest.current?.abort();
    operationsRequestSequence.current += 1;
    lastGoodRoute.current = null;
    setSelected(record);
    setSelectedOperations(emptySelectedOperations());
    setHistory([]);
    setHistoryCursor(null);
    setHistoryHasMore(false);
    setHistoryQuery(null);
    setHistoryOpen(false);
    setHistoryError("");
  }, [clearUpcomingPreview, selectedRouteSelectionKey, selectedVehicleId]);

  useEffect(() => {
    if (!upcomingPreview) return;
    const record = (fleet?.items || []).find((item) =>
      String(item.vehicle?.id || "") === upcomingPreview.vehicleId);
    if (!record || String(record.upcomingBooking?.id || "") !== upcomingPreview.bookingId ||
        String(record.activeBooking?.id || "") === upcomingPreview.bookingId) {
      clearUpcomingPreview();
    }
  }, [clearUpcomingPreview, fleet?.items, upcomingPreview?.bookingId, upcomingPreview?.vehicleId]);

  useEffect(() => {
    if (upcomingPreview && selectedVehicleId !== upcomingPreview.vehicleId) clearUpcomingPreview();
  }, [clearUpcomingPreview, selectedVehicleId, upcomingPreview?.vehicleId]);

  const toggleUpcomingRoute = useCallback(async (record, event) => {
    event.stopPropagation();
    const upcoming = record?.upcomingBooking;
    const vehicleId = String(record?.vehicle?.id || "");
    const bookingId = String(upcoming?.id || "");
    if (!vehicleId || !bookingId || upcoming?.routeAvailable !== true) return;
    if (upcomingPreview?.vehicleId === vehicleId &&
        upcomingPreview?.bookingId === bookingId &&
        Array.isArray(upcomingPreview.route) && upcomingPreview.route.length > 1) {
      clearUpcomingPreview();
      return;
    }
    selectVehicle(record);
    clearUpcomingPreview();
    upcomingPreviewIntent.current = { vehicleId, bookingId };
    const requestSequence = upcomingPreviewSequence.current;
    const controller = new AbortController();
    upcomingPreviewRequest.current = controller;
    setUpcomingPreview({ vehicleId, bookingId, loading: true, route: [] });
    try {
      let plannedRoute = null;
      try {
        const approved = await api.get(
          `/gps/bookings/${bookingId}/planned-routes/active`,
          { signal: controller.signal }
        );
        plannedRoute = approved.data?.route || null;
      } catch {
        // An approved route is optional; a read-only suggestion may still be available.
      }
      let route = providerRouteCoordinates(plannedRoute, { approved: true });
      if (route.length < 2) {
        const response = await api.get(
          `/gps/bookings/${bookingId}/planned-route-preview/read-only`,
          { params: { alternatives: 1 }, signal: controller.signal }
        );
        plannedRoute = previewRouteFromResponse(response.data, { allowSupportedProviders: true });
        route = providerRouteCoordinates(plannedRoute);
      }
      if (!mounted.current || controller.signal.aborted ||
          upcomingPreviewSequence.current !== requestSequence) return;
      if (route.length < 2) throw new Error("route_unavailable");
      setUpcomingPreview({
        vehicleId,
        bookingId,
        route,
        pickup: validCoordinates(upcoming.pickupCoords),
        destination: validCoordinates(upcoming.dropoffCoords),
        provider: plannedRoute?.provider || null,
        loading: false,
      });
    } catch {
      if (mounted.current && !controller.signal.aborted &&
          upcomingPreviewSequence.current === requestSequence) {
        setUpcomingPreview({ vehicleId, bookingId, loading: false, error: "Route unavailable", route: [] });
      }
    } finally {
      if (upcomingPreviewRequest.current === controller) upcomingPreviewRequest.current = null;
    }
  }, [clearUpcomingPreview, selectVehicle, upcomingPreview]);


  const monitoringRecords = useMemo(() => (fleet?.items || []).map((record) => ({
    ...record,
    monitoringBooking: bookingForVehicle(bookings, record),
  })).filter((record) => record?.source === "physical"), [bookings, fleet?.items]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return monitoringRecords.filter((record) => {
      if (record?.source !== "physical") return false;
      const text = [record.vehicle?.plateNumber, record.vehicle?.make, record.vehicle?.model,
        record.monitoringBooking?.bookingReference, record.monitoringBooking?.fullName,
        record.monitoringBooking?.userId?.name, record.monitoringBooking?.destination,
        record.upcomingBooking?.customerName]
        .filter(Boolean).join(" ").toLowerCase();
      if (query && !text.includes(query)) return false;
      if (statusFilter !== "all" && record.gpsStatus !== statusFilter) return false;
      return true;
    });
  }, [monitoringRecords, search, statusFilter]);

  useEffect(() => {
    setSelected((current) => {
      if (!current) return current;
      const refreshed = monitoringRecords.find((record) => record.vehicle?.id === current.vehicle?.id);
      return refreshed || null;
    });
  }, [monitoringRecords]);

  const openHistory = () => {
    const to = new Date();
    const from = new Date(to.getTime() - 24 * 60 * 60 * 1000);
    setHistoryFrom(from.toISOString().slice(0, 16));
    setHistoryTo(to.toISOString().slice(0, 16));
    setHistoryOpen(true);
    setHistory([]);
    setHistoryCursor(null);
    setHistoryHasMore(false);
    setHistoryQuery(null);
    setHistoryError("");
  };

  const loadHistory = async ({ append = false } = {}) => {
    if (!selected?.vehicle?.id || historyRequest.current) return;
    const from = append && historyQuery ? new Date(historyQuery.from) : new Date(historyFrom);
    const to = append && historyQuery ? new Date(historyQuery.to) : new Date(historyTo);
    if (!Number.isFinite(from.getTime()) || !Number.isFinite(to.getTime()) ||
        from >= to || to - from > MAX_HISTORY_MS) {
      setHistoryError("Choose a valid history range of no more than 7 days.");
      return;
    }
    const controller = new AbortController();
    historyRequest.current = controller;
    setHistoryLoading(true);
    setHistoryError("");
    try {
      const query = {
        from: from.toISOString(), to: to.toISOString(), limit: 1000,
        source: "physical",
      };
      if (append && historyCursor) query.cursor = historyCursor;
      const response = await api.get(`/gps/vehicles/${selected.vehicle.id}/history`, {
        params: {
          ...query,
        },
        signal: controller.signal,
      });
      if (mounted.current) {
        const positions = Array.isArray(response.data?.positions) ? response.data.positions : [];
        setHistory((current) => append ? [...current, ...positions] : positions);
        setHistoryCursor(response.data?.nextCursor || null);
        setHistoryHasMore(response.data?.hasMore === true && Boolean(response.data?.nextCursor));
        setHistoryQuery({ from: query.from, to: query.to, source: query.source });
      }
    } catch (caught) {
      if (mounted.current && caught?.code !== "ERR_CANCELED") {
        setHistoryError("Location history could not be loaded safely.");
      }
    } finally {
      historyRequest.current = null;
      if (mounted.current) setHistoryLoading(false);
    }
  };

  const summary = useMemo(() => {
    const records = monitoringRecords.filter((record) => record?.source === "physical");
    const states = records.map((record) => monitoringState(record));
    return {
      total: records.length,
      active: records.filter((record) => Boolean(record.monitoringBooking)).length,
      upcoming: Number.isSafeInteger(fleet?.summary?.upcoming)
        ? fleet.summary.upcoming
        : records.filter((record) => Boolean(record.upcomingBooking)).length,
      attention: states.filter((state) => state.key === "attention").length,
    };
  }, [bookings, monitoringRecords, fleet?.summary?.upcoming]);
  const allMapVehicles = useMemo(() => monitoringRecords.filter((record) =>
    Boolean(validCoordinates(record.coordinates))), [monitoringRecords]);
  const notificationAlertCount = !loading && fleet
    ? (Number.isSafeInteger(fleet.summary?.attention)
      ? fleet.summary.attention
      : summary.attention)
    : null;
  useMonitoringNotificationRefresh(notificationAlertCount);
  const applySavedAlertConfiguration = (configuration) => {
    setSelectedOperations((current) => Object.freeze({
      ...current,
      alertConfiguration: configuration,
      alertConfigurationPreview: null,
      routeMonitor: current.routeMonitor
        ? { ...current.routeMonitor, allowedRadiusMeters: configuration.deviationRadiusMeters }
        : current.routeMonitor,
    }));
    setBookings((current) => current.map((booking) =>
      String(booking?._id || booking?.id || "") === selectedBookingId
        ? { ...booking, gpsAlertConfiguration: configuration,
          tracking: { ...(booking.tracking || {}), allowedRadiusMeters: configuration.deviationRadiusMeters } }
        : booking
    ));
    setSelected((current) => {
      const booking = current?.monitoringBooking;
      return String(booking?._id || booking?.id || "") === selectedBookingId
        ? { ...current,
          routeDeviationAlert: configuration.alertsEnabled === false ? null : current.routeDeviationAlert,
          monitoringBooking: {
            ...booking,
            gpsAlertConfiguration: configuration,
            tracking: { ...(booking.tracking || {}), allowedRadiusMeters: configuration.deviationRadiusMeters },
          } }
        : current;
    });
  };
  const previewAlertConfiguration = (configuration) => {
    setSelectedOperations((current) => Object.freeze({
      ...current,
      alertConfigurationPreview: configuration,
    }));
  };
  const displayedAlertConfiguration = currentSelectedOperations.alertConfigurationPreview ||
    currentSelectedOperations.alertConfiguration || selected?.monitoringBooking?.gpsAlertConfiguration ||
    (selectedBookingId ? DEFAULT_BOOKING_ALERT_CONFIGURATION : null);
  const previewOnlySelectedRoute = Boolean(currentSelectedOperations.routePreview?.previewOnly) &&
    providerRouteCoordinates(currentSelectedOperations.plannedRoute, { approved: true }).length < 2 &&
    routeMonitorCoordinates(currentSelectedOperations.routeMonitor).length < 2;
  const selectedMonitoringState = selected ? monitoringState(selected) : null;
  const selectedAlertLifecycleEditable = ["active", "upcoming", "override", "attention"]
    .includes(selectedMonitoringState?.key);
  return (
    <AdminShell pageClassName="admin-shell--live-monitoring" eyebrow="FLEET MONITORING" title="Live Monitoring"
      subtitle="Monitor booked vehicles, route progress, and tracking readiness in real time."
      actions={!loading && !disabled ? <div className="gps-fleet-toolbar">
        <label className="gps-fleet-search">
          <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="10.8" cy="10.8" r="6.4" />
            <path d="m16 16 5 5" /></svg>
          <input aria-label="Search vehicles" type="search" value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search vehicles, customers, bookings, or destinations..." />
        </label>
        <select aria-label="GPS status filter" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
          <option value="all">All tracking statuses</option><option value="online">Online</option>
          <option value="stale">Needs attention</option><option value="offline">Offline</option>
          <option value="unknown">Unknown</option>
        </select>
        <button type="button" onClick={() => loadFleet()} disabled={refreshing}>
          {refreshing ? "Refreshing..." : "Refresh monitoring"}
        </button>
      </div> : null}>
      <div className="gps-fleet-dashboard">
        <GpsManagementTabs activeView={activeView} onViewChange={onViewChange} />
        {disabled ? <div role="status" className="gps-fleet-state gps-fleet-state--disabled">{GPS_DISABLED_MESSAGE}</div> : null}
        {error ? <div role="alert" className="gps-fleet-state gps-fleet-state--error">{error}</div> : null}
        {loading ? <div role="status" className="gps-fleet-state">Loading live fleet monitoring...</div> : null}

        {!loading && !disabled ? (
          <>
            <div className="gps-fleet-summary" aria-label="Fleet summary">
              {[ 
                ["Active now", summary.active, "active"], ["Upcoming bookings", summary.upcoming, "upcoming"],
                ["All tracked vehicles", summary.total, "tracked"], ["Attention", summary.attention, "attention"],
              ].map(([label, value, tone]) => <div key={label} className={`gps-fleet-summary-card gps-fleet-summary-card--${tone}`}>
                <i aria-hidden="true"><FleetSummaryIcon tone={tone} /></i>
                <span>{label}</span><strong>{value ?? 0}</strong>
                <small>{tone === "upcoming" ? (value === 1 ? "booking" : "bookings") :
                  value === 1 ? "vehicle" : "vehicles"}</small>
              </div>)}
            </div>

            <div className="gps-fleet-layout">
              <section className="gps-fleet-list" aria-label="Vehicle list">
                <header><div><h2>Vehicles & bookings</h2><small>Select a vehicle to focus the map</small></div><span>{filtered.length}</span></header>
                <div className="gps-fleet-list-scroll">
                  <button type="button" className={`gps-fleet-all-card ${selected ? "" : "is-selected"}`}
                    aria-pressed={!selected} onClick={selectAllVehicles}>
                    <span className="gps-fleet-all-card__icon" aria-hidden="true">
                      <svg viewBox="0 0 56 56" fill="none">
                        <ellipse cx="28" cy="48" rx="14" ry="4.5" className="gps-fleet-all-card__shadow" />
                        <path
                          className="gps-fleet-all-card__pin"
                          d="M28 3.5c-9.39 0-17 7.61-17 17 0 12.2 17 29 17 29s17-16.8 17-29c0-9.39-7.61-17-17-17Z"
                        />
                        <path
                          className="gps-fleet-all-card__car"
                          d="m21.4 21.8 1.7-4.8a2 2 0 0 1 1.9-1.3h6a2 2 0 0 1 1.9 1.3l1.7 4.8 1.2 1.2v5.2a1.4 1.4 0 0 1-1.4 1.4h-1.2v-1.5h-9.8v1.5h-1.2a1.4 1.4 0 0 1-1.4-1.4V23l1.5-1.2Z"
                        />
                        <path
                          className="gps-fleet-all-card__window"
                          d="m24.1 17.4-1.2 3.5h10.2l-1.2-3.5a.8.8 0 0 0-.8-.5h-6.2a.8.8 0 0 0-.8.5Z"
                        />
                        <path className="gps-fleet-all-card__wheels" d="M22.6 24.5h1.2m8.4 0h1.2" />
                      </svg>
                    </span>
                    <span className="gps-fleet-all-card__copy">
                      <strong>All vehicles</strong>
                      <b>{allMapVehicles.length} tracked {allMapVehicles.length === 1 ? "vehicle" : "vehicles"}</b>
                      <small>Show all vehicles on the map</small>
                    </span>
                    <span className="gps-fleet-all-card__chevron" aria-hidden="true">›</span>
                  </button>
                  {filtered.length ? filtered.map((record) => {
                    const booking = record.monitoringBooking;
                    const state = monitoringState(record);
                    const severity = getMonitoringSeverity(record);
                    const gpsTone = gpsDisplayTone(record);
                    const severityTone = severity.gpsOffline && gpsTone === "caution"
                      ? "caution" : severity.key === "normal" ? gpsTone : severity.key;
                    const statusLabel = severity.gpsOffline ? gpsDisplayLabel(record) :
                      severity.label || gpsDisplayLabel(record);
                    const customer = booking?.fullName || booking?.customerName || booking?.userId?.name ||
                      (record.activeBooking ? "Customer unavailable" : "No active booking");
                    const upcoming = record.upcomingBooking;
                    const cardPreview = upcomingPreview?.vehicleId === String(record.vehicle?.id || "") &&
                      upcomingPreview?.bookingId === String(upcoming?.id || "") ? upcomingPreview : null;
                    const routeVisible = Array.isArray(cardPreview?.route) && cardPreview.route.length > 1;
                    return <div role="button" tabIndex={0} key={record.vehicle.id}
                      className={`gps-fleet-list-item gps-fleet-list-item--${state.key} gps-fleet-list-item--severity-${severity.key} ${selected?.vehicle?.id === record.vehicle.id ? "is-selected" : ""}`}
                      data-gps-tone={gpsTone}
                      onClick={() => selectVehicle(record, { focusMap: true })}
                      onKeyDown={(event) => {
                        if (event.target === event.currentTarget && (event.key === "Enter" || event.key === " ")) {
                          event.preventDefault();
                          selectVehicle(record, { focusMap: true });
                        }
                      }}>
                      <VehicleAvatar vehicle={record.vehicle} className="gps-fleet-list-avatar" />
                      <span className="gps-fleet-list-copy">
                        <span className="gps-fleet-list-title"><strong>{[record.vehicle?.make, record.vehicle?.model].filter(Boolean).join(" ") || NOT_AVAILABLE}</strong>
                          {record.activeBooking ? <em className="gps-fleet-booking-chip">Active booking</em> : null}</span>
                        <small className="gps-fleet-plate">{record.vehicle?.plateNumber || NOT_AVAILABLE}</small>
                        <span className="gps-fleet-state-row">
                          <em className={`gps-monitor-chip gps-monitor-chip--${severityTone}`}>
                            {statusLabel}</em>
                        </span>
                        {booking || !upcoming ? <small>{customer}</small> : null}
                        {booking ? <small className="gps-fleet-schedule">{formatSchedule(booking)}</small> : null}

                        {state.manualOverride ? <span className="gps-fleet-card-flags">
                          <em className="is-override">Manual override enabled</em></span> : null}
                      </span>
                        {upcoming ? <span className="gps-fleet-upcoming">
                          <strong>{record.activeBooking ? "Next booking" : "Upcoming booking"}</strong>
                          <time dateTime={upcoming.startAt}>{formatUpcomingStart(upcoming.startAt)}</time>
                          <small>{upcoming.customerName || "Customer unavailable"}</small>
                          {upcoming.routeAvailable ? <button type="button" className="gps-fleet-upcoming-action"
                            disabled={cardPreview?.loading === true}
                            onClick={(event) => toggleUpcomingRoute(record, event)}>
                            {cardPreview?.loading ? "Loading route..." : routeVisible ? "Hide Route" : "View Route"}
                          </button> : <em>Route unavailable</em>}
                          {cardPreview?.error ? <em role="status">{cardPreview.error}</em> : null}
                        </span> : null}
                      <span className="gps-fleet-list-chevron" aria-hidden="true">›</span>
                    </div>;
                  }) : <div className="gps-fleet-empty">No vehicles match the selected filters.</div>}
                </div>
              </section>

              <div className="gps-fleet-map-column">
                <FleetMap selected={selected} allVehicles={allMapVehicles} history={history} operations={currentSelectedOperations}
                  upcomingPreview={currentUpcomingPreview}
                  mapSectionRef={mapSectionRef} vehicleFocus={vehicleFocus} fleetFitRequest={fleetFitRequest}
                  onSelectVehicle={selectVehicle}
                  onRouteRetry={retrySelectedRoute}
                  alertControls={previewOnlySelectedRoute
                    ? <div className="gps-fleet-map-context"><span>Route monitoring</span>
                      <strong>Setup unavailable for preview</strong></div>
                    : selectedBookingId ? <BookingAlertConfigurationPanel
                    key={selectedBookingId}
                    bookingId={selectedBookingId}
                    configuration={currentSelectedOperations.alertConfiguration ||
                      selected?.monitoringBooking?.gpsAlertConfiguration || null}
                    limits={currentSelectedOperations.alertConfigurationLimits}
                    lifecycleEditable={selectedAlertLifecycleEditable}
                    onPreview={previewAlertConfiguration}
                    onSaved={applySavedAlertConfiguration}
                  /> : null} />
                {selected ? <section className="gps-fleet-details" aria-label="Selected vehicle details">
                  <div className="gps-fleet-detail-primary">
                    <h3>Booking details</h3>
                    <DetailRow label="Customer" value={selected.monitoringBooking?.fullName || selected.monitoringBooking?.customerName || selected.monitoringBooking?.userId?.name || NOT_AVAILABLE} />
                    <DetailRow label="Booking schedule" value={formatSchedule(selected.monitoringBooking)} />
                    <DetailRow label="Pickup" value={selected.monitoringBooking?.pickupLocation || NOT_AVAILABLE} />
                    <DetailRow label="Destination / Drop-off" value={selected.monitoringBooking?.destination ||
                      (selectedBookingId ? currentSelectedOperations.plannedRoute?.destination?.address : null) || NOT_AVAILABLE} />
                  </div>
                  <div className="gps-fleet-detail-live">
                    <h3>Vehicle & tracking</h3>
                    <DetailRow label="Vehicle movement" value={titleCase(selected.movementStatus)} />
                    <DetailRow label="GPS status" value={titleCase(displayGpsStatus(selected))}
                      tone={gpsDisplayTone(selected)} />
                    <DetailRow label="Power status" value={selected.powerDisconnected ? "Disconnected" : "Connected"}
                      tone={selected.powerDisconnected ? "critical" : "healthy"} />
                    <DetailRow label="Alert mode" value={titleCase(displayedAlertConfiguration?.mode)} />
                    {previewOnlySelectedRoute ? <DetailRow label="Route monitoring" value="Unavailable for route preview" /> : null}
                    <DetailRow label="Route deviation radius" value={formatMeters(displayedAlertConfiguration?.deviationRadiusMeters ?? currentSelectedOperations.routeMonitor?.allowedRadiusMeters ?? selected.monitoringBooking?.tracking?.allowedRadiusMeters)} />
                    <DetailRow label="GPS offline threshold" value={formatSeconds(monitoringPolicy.gpsOfflineThresholdSeconds)} />
                    <DetailRow label="Current speed" value={formatValue(selected.speedKph, " km/h")} />
                    <DetailRow label="Last update" value={formatTime(selected.recordedAt)} />
                  </div>
                  {hasActiveRouteDeviation(selected) && !previewOnlySelectedRoute ?
                    <section className="gps-route-deviation-alert gps-route-deviation-alert--warning" aria-label="Active route deviation alert">
                      <strong>Route deviation detected</strong>
                      <DetailRow label="Alert status" value="Active" />
                      <DetailRow label="Vehicle" value={[
                        selected.vehicle?.make, selected.vehicle?.model, selected.vehicle?.plateNumber,
                      ].filter(Boolean).join(" · ") || NOT_AVAILABLE} />
                      <DetailRow label="Detected" value={formatTime(selected.routeDeviationAlert.detectedAt)} />
                      <DetailRow label="Distance outside allowed radius"
                        value={formatMeters(selected.routeDeviationAlert.distanceOutsideAllowedRadiusMeters)} />
                    </section> : null}
                  <div className="gps-fleet-detail-actions">
                    <button type="button" onClick={() => loadSelected()}>Refresh vehicle</button>
                    {diagnosticTelemetryEnabled ? <button type="button" onClick={openHistory}>View diagnostic telemetry</button> : null}
                  </div>
                </section> : null}
              </div>
            </div>

            {historyOpen ? <section className="gps-fleet-history" aria-label="Diagnostic telemetry">
              <header><div><h2>Diagnostic telemetry</h2><p>Raw tracker points load only when requested. This is not operator trip history.</p></div>
                <button type="button" onClick={() => { historyRequest.current?.abort(); setHistoryOpen(false); setHistory([]); setHistoryCursor(null); setHistoryHasMore(false); setHistoryQuery(null); }}>Close</button></header>
              <div className="gps-fleet-history-controls">
                <label>From<input aria-label="History from" type="datetime-local" value={historyFrom} onChange={(event) => setHistoryFrom(event.target.value)} /></label>
                <label>To<input aria-label="History to" type="datetime-local" value={historyTo} onChange={(event) => setHistoryTo(event.target.value)} /></label>
                <button type="button" onClick={() => loadHistory()} disabled={historyLoading}>{historyLoading ? "Loading..." : "Load telemetry"}</button>
              </div>
              {historyError ? <div role="alert">{historyError}</div> : null}
              {!historyLoading && !historyError && history.length === 0 ? <p>No history positions were found for this range.</p> : null}
              {history.length ? <div className="gps-fleet-history-list">{history.map((position, index) => (
                <div key={`${position.recordedAt}-${index}`}><strong>{formatTime(position.recordedAt)}</strong>
                  <span>{formatValue(position.speedKph, " km/h")}</span></div>
              ))}</div> : null}
              {historyHasMore ? <button type="button" onClick={() => loadHistory({ append: true })} disabled={historyLoading}>
                {historyLoading ? "Loading..." : "Load more telemetry"}
              </button> : null}
            </section> : null}
          </>
        ) : null}
      </div>
    </AdminShell>
  );
}
